<template>
  <div
    class="w-full translate-x-full opacity-0 tr-all"
    :class="{ '!translate-x-0 opacity-100': state.isVisible }"
    @mouseenter="pauseTimeout"
    @mouseleave="resumeTimeout">
    <div
      class="w-full relative p-3 rounded-lg shadow-lg overflow-hidden"
      :style="notificationTypeClasses">
      <div
        class="absolute -top-0.5 right-1 text-lg text-white hover:text-black tr-all cursor-pointer"
        @click="closeNotification">
        ✖
      </div>
      <div class="w-full text-white text-sm font-medium">
        {{ notification.message }}
      </div>
      <div
        class="absolute bottom-0 left-0 h-1 bg-white opacity-50 transition-all ease-linear"
        :style="{ width: `${state.progress}%` }"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, reactive, onUnmounted } from 'vue';
import { NotificationsItemType } from '@/types';
import useNotificationsStore from '@/store/notifications';

const notificationsStore = useNotificationsStore();

const props = defineProps<{ notification: NotificationsItemType; index: number }>();

const notificationColors = {
success: '#1b75bb',
error: '#c10007',
info: '#0069a8',
warning: '#ca3500'
};

const durationMs = (props.notification.hide_after_sec ?? 5) * 1000;

const state = reactive({
  isVisible: false,
  progress: 100,
  timeoutId: null as NodeJS.Timeout | null,
  startTime: 0,
  duration: durationMs,
  remainingTime: durationMs,
  isPaused: false,
});

const closeNotification = () => {
  if (state.timeoutId) {
    clearTimeout(state.timeoutId);
  }
  notificationsStore.remove(props.index);
};

const notificationTypeClasses = computed(() => {
  return `background: ${notificationColors[props.notification.type] || '#0069a8'}`;
});

const startProgressBar = () => {
  let lastTime = Date.now();

  const updateProgress = () => {
    if (state.isPaused) return;

    const now = Date.now();
    state.remainingTime -= now - lastTime;
    lastTime = now;
    state.progress = Math.max(0, (state.remainingTime / state.duration) * 100);

    if (state.remainingTime > 0) {
      requestAnimationFrame(updateProgress);
    } else {
      closeNotification();
    }
  };

  requestAnimationFrame(updateProgress);
};

const pauseTimeout = () => {
  state.isPaused = true;
};

const resumeTimeout = () => {
  if (state.isPaused) {
    state.isPaused = false;
    state.startTime = Date.now() - (state.duration - state.remainingTime);
    startProgressBar();
  }
};

onMounted(() => {
  setTimeout(() => {
    state.isVisible = true;
    startProgressBar();
  }, 10);
});

onUnmounted(() => {
  if (state.timeoutId) {
    clearTimeout(state.timeoutId);
  }
});
</script>
